/**
 * Tiny observable for the Admin service's reachability, so the UI can show a
 * "waking the admin service…" state during a scale-to-zero cold start.
 *
 * - `idle`   — no request has been made yet
 * - `waking` — a request is being retried because the host is cold-starting
 * - `ready`  — a request has succeeded; the host is warm
 * - `error`  — retries were exhausted
 *
 * Framework-agnostic (plain subscribe/getSnapshot) so it is unit-testable and
 * consumable from React via `useSyncExternalStore`.
 */
export type ServiceStatus = 'idle' | 'waking' | 'ready' | 'error';

export interface ServiceStatusStore {
    subscribe(listener: () => void): () => void;
    getSnapshot(): ServiceStatus;
    set(status: ServiceStatus): void;
}

export function createServiceStatusStore(initial: ServiceStatus = 'idle'): ServiceStatusStore {
    let status = initial;
    const listeners = new Set<() => void>();
    return {
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        getSnapshot() {
            return status;
        },
        set(next) {
            if (next === status) return;
            status = next;
            listeners.forEach((l) => l());
        },
    };
}
